// MYG-151497: "Sign in with a QR code" widget for the Drive login page (Tesla Semi IVI).
// Plain DOM on purpose: the live login is a Backbone view, and this keeps the widget independent of it
// so it can be mounted by the React login page later. Nothing the identity provider sends is rendered as text
// or markup; the QR image is generated here from the verification address. The QR code is
// the only way to approve a sign-in: there is no typed-code fallback, because the verification page is not
// reachable from a short address a driver could type.

import QRCode from "qrcode";
import {
    DeviceAuthError,
    DeviceAuthErrorCode,
    IDeviceAuthConfig,
    IDeviceAuthPoll,
    IDeviceAuthTokens,
    pollDeviceAuth,
    revokeDeviceAuthTokens,
    startDeviceAuth
} from "../../services/deviceAuthClient";
import LoginTranslations from "../../helpers/translations";

const QR_IMAGE_SIZE_PX = 260;
// Errors a second try cannot fix: the realm or client is not set up for this, or the response cannot be trusted.
const NON_RETRYABLE_ERROR_CODES: string[] = [
    DeviceAuthErrorCode.NotConfigured,
    DeviceAuthErrorCode.PkceUnavailable,
    DeviceAuthErrorCode.UntrustedVerificationUri,
    "invalid_client",
    "unauthorized_client",
    "unsupported_grant_type"
];

export interface IQrLoginPanelOptions {
    getConfig: () => Promise<IDeviceAuthConfig>;
    /** Called once the driver approved the code. `config` is the realm and client the tokens were issued for. */
    onTokens: (tokens: IDeviceAuthTokens, config: IDeviceAuthConfig) => void;
    onError?: (error: unknown) => void;
    /** Synchronous translator for localizing labels. Falls back to English when omitted. */
    translate?: (key: string) => string;
}

/** True when trying again could succeed; false when the option cannot work here at all. */
export function isRetryableQrError(error: unknown): boolean {
    return !(error instanceof DeviceAuthError && NON_RETRYABLE_ERROR_CODES.indexOf(error.code) > -1);
}

export function getQrErrorKey(error: unknown): string {
    if (error instanceof DeviceAuthError) {
        if (NON_RETRYABLE_ERROR_CODES.indexOf(error.code) > -1) {
            return LoginTranslations.QrUnavailable;
        }

        switch (error.code) {
            case DeviceAuthErrorCode.Expired:
                return LoginTranslations.QrExpired;
            case DeviceAuthErrorCode.Network:
                return LoginTranslations.QrNetworkError;
            case "access_denied":
                return LoginTranslations.QrDenied;
            default:
                return LoginTranslations.QrFailed;
        }
    }
    return LoginTranslations.QrFailed;
}

/** @deprecated Use `getQrErrorKey` with a translator instead. Retained for tests that check English strings directly. */
export function getQrErrorMessage(error: unknown): string {
    return getQrErrorKey(error);
}

export class QrLoginPanel {
    readonly el: HTMLElement;
    // The panel is [view][status][actions]. Only view and actions are replaced on a state change; the status line is
    // one persistent element, because a screen reader announces changes to a live region, not a new one.
    private readonly view: HTMLElement;
    private readonly statusEl: HTMLElement;
    private readonly actions: HTMLElement;
    private poll: IDeviceAuthPoll | undefined;
    private isDestroyed = false;
    private isIdle = true;
    // Guards against a start() that resolves after the user cancelled or the panel was removed.
    private attempt = 0;

    private readonly t: (key: string) => string;

    constructor(private readonly options: IQrLoginPanelOptions) {
        this.t = options.translate ?? ((key: string) => key);
        this.el = document.createElement("div");
        this.el.id = "login-qr-panel";

        this.view = document.createElement("div");
        this.actions = document.createElement("div");
        this.statusEl = document.createElement("p");
        this.statusEl.id = "login-qr-status";
        this.statusEl.setAttribute("role", "status");
        this.statusEl.setAttribute("aria-live", "polite");

        this.el.appendChild(this.view);
        this.el.appendChild(this.statusEl);
        this.el.appendChild(this.actions);
        this.renderIdle();
    }

    /**
     * Hiding the panel (the login page moved on: username step, progress, error) abandons any code that is
     * still pending, so an approval that lands later cannot start a second login behind the user's back.
     */
    setVisible(isVisible: boolean) {
        !isVisible && !this.isIdle && this.reset();
        this.el.style.display = isVisible ? "" : "none";
    }

    /** Abandons the current attempt, if any, and goes back to the start button. */
    reset() {
        this.abort();
        this.renderIdle();
    }

    destroy() {
        this.isDestroyed = true;
        this.abort();
        this.el.remove();
    }

    private abort() {
        this.attempt++;
        this.poll?.cancel();
        this.poll = undefined;
    }

    private async start() {
        const attempt = ++this.attempt;
        this.renderRequesting();
        let config: IDeviceAuthConfig | undefined;
        let tokens: IDeviceAuthTokens | undefined;

        try {
            config = await this.options.getConfig();
            if (this.isDestroyed || attempt !== this.attempt) {
                return;
            }

            const start = await startDeviceAuth(config);
            const qrImageSource = await QRCode.toDataURL(start.verificationUriComplete, {
                errorCorrectionLevel: "M",
                margin: 1,
                width: QR_IMAGE_SIZE_PX
            });
            if (this.isDestroyed || attempt !== this.attempt) {
                return;
            }

            this.renderWaiting(qrImageSource);
            this.poll = pollDeviceAuth(config, start);
            tokens = await this.poll.promise;
            if (this.isDestroyed || attempt !== this.attempt) {
                // Approved on the phone, but the driver already moved on. These tokens will never be used, and they
                // include offline_access, so do not leave them alive at the identity provider.
                void revokeDeviceAuthTokens(config, tokens);
                return;
            }

            this.renderMessage(this.t(LoginTranslations.QrSigningIn));
            this.options.onTokens(tokens, config);
        } catch (error) {
            const isCancelled = error instanceof DeviceAuthError && error.code === DeviceAuthErrorCode.Cancelled;
            if (this.isDestroyed || isCancelled || attempt !== this.attempt) {
                return;
            }
            this.options.onError?.(error);
            this.renderError(error);
        }
    }

    private renderIdle() {
        this.render(() => {
            this.isIdle = true;
            this.setStatus("");
            this.actions.appendChild(this.createButton("login-qr-start", this.t(LoginTranslations.QrSignInWithQrCode), () => void this.start(), true));
        });
    }

    private renderRequesting() {
        this.render(() => {
            this.isIdle = false;
            this.setStatus(this.t(LoginTranslations.QrRequesting));
            this.actions.appendChild(this.createButton("login-qr-cancel", this.t(LoginTranslations.Cancel), () => this.reset(), false));
        });
    }

    private renderMessage(text: string) {
        this.render(() => {
            this.isIdle = false;
            this.setStatus(text);
        });
    }

    private renderWaiting(qrImageSource: string) {
        this.render(() => {
            this.isIdle = false;

            const image = document.createElement("img");
            image.id = "login-qr-image";
            image.src = qrImageSource;
            image.alt = this.t(LoginTranslations.QrImageAlt);
            image.width = QR_IMAGE_SIZE_PX;
            image.height = QR_IMAGE_SIZE_PX;
            this.view.appendChild(image);

            this.setStatus(this.t(LoginTranslations.QrScanInstruction));
            this.actions.appendChild(this.createButton("login-qr-cancel", this.t(LoginTranslations.Cancel), () => this.reset(), false));
        });
    }

    private renderError(error: unknown) {
        this.render(() => {
            this.isIdle = false;
            this.setStatus("");

            const message = document.createElement("p");
            message.id = "login-qr-error";
            message.setAttribute("role", "alert");
            message.textContent = this.t(getQrErrorKey(error));
            this.view.appendChild(message);

            isRetryableQrError(error) && this.actions.appendChild(this.createButton("login-qr-retry", this.t(LoginTranslations.QrTryAgain), () => void this.start(), true));
        });
    }

    /**
     * Replaces the panel's content. If the driver (or a screen reader) had focus inside the panel, it moves to the new
     * primary button instead of being dropped on the page, because the button that held it is gone.
     */
    private render(build: () => void) {
        const hadFocus = this.el.contains(document.activeElement);
        this.clear(this.view);
        this.clear(this.actions);
        build();
        hadFocus && this.actions.querySelector("button")?.focus();
    }

    private setStatus(text: string) {
        this.statusEl.textContent = text;
    }

    private clear(element: HTMLElement) {
        while (element.firstChild) {
            element.removeChild(element.firstChild);
        }
    }

    private createButton(id: string, text: string, onClick: () => void, isPrimary: boolean): HTMLButtonElement {
        const button = document.createElement("button");
        button.type = "button";
        button.id = id;
        button.className = isPrimary ? "button button_inline" : "button button_inline button_secondary";
        button.textContent = text;
        button.addEventListener("click", onClick);
        return button;
    }
}
